// src/pages/RegionDashboard.js - Improved version
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Box,
  Divider,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  useTheme,
  alpha,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Storage as ServerIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Memory as MemoryIcon,
  DeveloperBoard as CpuIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  DateRange as DateRangeIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';

// Import required Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Import custom hooks and components
import { useAppData } from '../contexts/AppDataContext';
import PageHeader from '../components/common/PageHeader';
import LoadingOverlay from '../components/common/LoadingOverlay';

// Import client name mapping
import { getFullClientName, isExcludedInternalClient } from '../utils/clientNameMappings';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

// Modern Metric Card component (consistent with Dashboard style)
// Modified ModernMetricCard component with error handling
const ModernMetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary', onClick }) => {
    const theme = useTheme();
    
    // Safely get color - handle case when color is not in palette
    const getColorSafely = (colorName) => {
      // Check if the color exists in the theme palette
      if (theme.palette[colorName] && theme.palette[colorName].main) {
        return theme.palette[colorName].main;
      }
      // Fallback to primary color if the specified color doesn't exist
      return theme.palette.primary.main;
    };
    
    // Get alpha color safely
    const getAlphaSafely = (colorName, alphaValue) => {
      const colorMain = getColorSafely(colorName);
      return alpha(colorMain, alphaValue);
    };
    
    return (
      <Card 
        sx={{ 
          height: '100%',
          borderRadius: '16px',
          background: `linear-gradient(145deg, ${getAlphaSafely(color, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
          boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
          backdropFilter: 'blur(8px)',
          transition: 'all 0.3s ease',
          cursor: onClick ? 'pointer' : 'default',
          overflow: 'hidden',
          '&:hover': onClick ? {
            transform: 'translateY(-10px)',
            boxShadow: `0 15px 30px ${alpha(theme.palette.common.black, 0.12)}`,
            '& .icon-bg': {
              transform: 'scale(1.1)',
            }
          } : {},
        }}
        onClick={onClick}
      >
        <Box 
          className="icon-bg"
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${getAlphaSafely(color, 0.05)} 0%, ${getAlphaSafely(color, 0.02)} 100%)`,
            transition: 'transform 0.3s ease',
          }}
        />
        <Box sx={{ position: 'relative', height: '100%', p: 3, pb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography 
                variant="h6" 
                fontWeight="medium" 
                sx={{ 
                  color: theme.palette.text.primary,
                  fontSize: '1.1rem',
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha(theme.palette.text.secondary, 0.8),
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            </Box>
            <Box 
              sx={{
                backgroundColor: getAlphaSafely(color, 0.1),
                color: getColorSafely(color),
                borderRadius: '12px',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon sx={{ fontSize: 24 }} />
            </Box>
          </Box>
          
          <Box display="flex" alignItems="flex-end" justifyContent="space-between">
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              sx={{ 
                color: getColorSafely(color),
                fontSize: '2.5rem',
              }}
            >
              {value}
            </Typography>
            
            {trend && trendValue && (
              <Chip
                label={
                  <Box display="flex" alignItems="center">
                    {trend === 'up' ? '↑' : '↓'}
                    <Typography variant="caption" fontWeight="bold" sx={{ ml: 0.5 }}>
                      {trendValue}
                    </Typography>
                  </Box>
                }
                sx={{
                  backgroundColor: trend === 'up' 
                    ? alpha(theme.palette.success.main, 0.1) 
                    : alpha(theme.palette.error.main, 0.1),
                  color: trend === 'up' 
                    ? theme.palette.success.main 
                    : theme.palette.error.main,
                  fontWeight: 'bold',
                  borderRadius: '10px',
                  height: 24,
                }}
                size="small"
              />
            )}
          </Box>
        </Box>
      </Card>
    );
  };

// Modern Chart Card component (consistent with Dashboard style)
const ModernChartCard = ({ title, subtitle, chart, height = 320, actions }) => {
  const theme = useTheme();
  
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '16px',
        background: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: 'blur(8px)',
        boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="medium">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {actions}
          </Box>
        </Box>
        
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            height: height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {chart}
        </Box>
      </Box>
    </Card>
  );
};

// Status chip component
const StatusChip = ({ status, size = 'medium', sx = {} }) => {
  const theme = useTheme();
  const isActive = status === 'active';
  
  return (
    <Chip
      label={isActive ? 'Active' : 'Inactive'}
      size={size}
      sx={{
        backgroundColor: isActive 
          ? alpha(theme.palette.success.main, 0.1) 
          : alpha(theme.palette.error.main, 0.1),
        color: isActive 
          ? theme.palette.success.main 
          : theme.palette.error.main,
        fontWeight: 'medium',
        ...sx
      }}
    />
  );
};

export default function RegionDashboard() {
  const { regionId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State
  const [timeRange, setTimeRange] = useState('24h');
  const [host, setHost] = useState('');
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [dataVersion, setDataVersion] = useState(0); // Add version for controlled updates
  
  // Refs for tracking mount state and debouncing
  const isMounted = useRef(true);
  const dataLoadTimerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  // Get data from context
  const { 
    summary,
    regions,
    hosts,
    systemMetrics,
    clientLogs,
    hostClients,
    clientDetails,
    loading,
    fetchRegions,
    fetchHosts,
    fetchSummary,
    fetchSystemMetrics,
    fetchClientLogs,
    fetchHostClients,
    fetchClientDetails,
    refreshData
  } = useAppData();
  
  // Load dashboard data with optimized loading strategy
  useEffect(() => {
    isMounted.current = true;
    
    const loadData = async () => {
      if (!regionId) return;
      
      setIsDataLoaded(false);
      
      try {
        // Clear any pending timer
        if (dataLoadTimerRef.current) {
          clearTimeout(dataLoadTimerRef.current);
        }
        
        // Use small timeout to ensure component is fully mounted
        dataLoadTimerRef.current = setTimeout(async () => {
          try {
            // First load critical data
            await Promise.all([
              fetchHosts(regionId),
              fetchSummary(regionId)
            ]);
            
            // Then load chart data in parallel
            const chartDataPromises = [
              fetchSystemMetrics({ range: timeRange, region: regionId, host }),
              fetchClientLogs({ range: timeRange, region: regionId, host }),
              fetchHostClients(regionId)
            ];
            
            // Start loading chart data
            Promise.all(chartDataPromises).then(() => {
              // Finally load detailed data
              fetchClientDetails(regionId);
            });
            
            if (isMounted.current) {
              setIsDataLoaded(true);
              setDataVersion(v => v + 1); // Trigger controlled re-render
            }
          } catch (error) {
            console.error('Error loading region data:', error);
            if (isMounted.current) {
              setIsDataLoaded(true);
            }
          }
        }, 50);
      } catch (error) {
        console.error('Error loading region data:', error);
        if (isMounted.current) {
          setIsDataLoaded(true);
        }
      }
    };
    
    loadData();
    
    // Cleanup function
    return () => {
      isMounted.current = false;
      if (dataLoadTimerRef.current) {
        clearTimeout(dataLoadTimerRef.current);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [regionId, timeRange, host, fetchHosts, fetchSummary, fetchSystemMetrics, fetchClientLogs, fetchHostClients, fetchClientDetails]);
  
  // Debounced refresh handler
  const handleRefresh = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      setIsDataLoaded(false);
      refreshData({ 
        region: regionId, 
        timeRange, 
        host,
        forceRefresh: true 
      }).then(() => {
        if (isMounted.current) {
          setIsDataLoaded(true);
          setDataVersion(v => v + 1);
        }
      }).catch(error => {
        console.error('Error refreshing data:', error);
        if (isMounted.current) {
          setIsDataLoaded(true);
        }
      });
    }, 300);
  }, [refreshData, regionId, timeRange, host]);
  
  // Handle back to dashboard
  const handleBackToDashboard = () => {
    navigate(`/`);
  };
  
  // Optimized host filter
  const regionHosts = useMemo(() => {
    if (!hosts || !regionId) return [];
    return hosts.filter(hostname => hostname.startsWith(regionId));
  }, [hosts, regionId]);
  
  // Optimized system metrics data preparation
  const systemMetricsData = useMemo(() => {
    if (!systemMetrics?.cpu?.length) return null;
    
    const uniqueHosts = new Set();
    const timeLabels = new Set();
    const dataByHost = new Map();
    
    // Pre-process data
    systemMetrics.cpu.forEach(item => {
      uniqueHosts.add(item.hostname);
      timeLabels.add(new Date(item.time).toLocaleTimeString());
      
      if (!dataByHost.has(item.hostname)) {
        dataByHost.set(item.hostname, []);
      }
      dataByHost.get(item.hostname).push(item.value);
    });
    
    // Convert to array and sort labels
    const sortedLabels = Array.from(timeLabels).sort();
    const sampledLabels = sortedLabels.filter((_, i) => i % Math.ceil(sortedLabels.length / 20) === 0);
    
    return {
      labels: sampledLabels,
      datasets: Array.from(uniqueHosts).map((hostname, index) => ({
        label: `${hostname} - CPU`,
        data: dataByHost.get(hostname),
        borderColor: getColor(index),
        backgroundColor: getColor(index, 0.2),
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
      })),
    };
  }, [systemMetrics, dataVersion]);
  
  // Optimized memory metrics data preparation
  const memoryMetricsData = useMemo(() => {
    if (!systemMetrics?.memory?.length) return null;
    
    const uniqueHosts = new Set();
    const timeLabels = new Set();
    const dataByHost = new Map();
    
    // Pre-process data
    systemMetrics.memory.forEach(item => {
      uniqueHosts.add(item.hostname);
      timeLabels.add(new Date(item.time).toLocaleTimeString());
      
      if (!dataByHost.has(item.hostname)) {
        dataByHost.set(item.hostname, []);
      }
      dataByHost.get(item.hostname).push(item.value);
    });
    
    // Convert to array and sort labels
    const sortedLabels = Array.from(timeLabels).sort();
    const sampledLabels = sortedLabels.filter((_, i) => i % Math.ceil(sortedLabels.length / 20) === 0);
    
    return {
      labels: sampledLabels,
      datasets: Array.from(uniqueHosts).map((hostname, index) => ({
        label: `${hostname} - Memory`,
        data: dataByHost.get(hostname),
        borderColor: getColor(index),
        backgroundColor: getColor(index, 0.2),
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
      })),
    };
  }, [systemMetrics, dataVersion]);
  
  // Optimized client log data preparation
  const clientLogData = useMemo(() => {
    if (!clientLogs?.by_client?.length) return null;
    
    const topClients = clientLogs.by_client
      .filter((item) => !isExcludedInternalClient(item.client_name))
      .slice(0, 10)
      .map(item => ({
        name: item.client_name,
        size: item.size_gb
      }));
    
    return {
      labels: topClients.map(client => client.name),
      datasets: [{
        label: 'Log Size (GB)',
        data: topClients.map(client => client.size),
        backgroundColor: Array(10).fill().map((_, i) => getColor(i, 0.7)),
        borderColor: Array(10).fill().map((_, i) => getColor(i)),
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  }, [clientLogs, dataVersion]);
  
  // Optimized product log data preparation
  const productLogData = useMemo(() => {
    if (!clientLogs?.by_product?.length) return null;

    const topProducts = clientLogs.by_product
      .filter(item => {
        if (isExcludedInternalClient(item.client_name)) return false;
        const name = (item?.product || '').trim().toLowerCase();
        return name !== '' && name !== 'unknown' && name !== 'unknownproduct';
      })
      .slice(0, 10)
      .map(item => ({
        label: `${item.product || 'Unknown'} (${item.client_name || 'Unknown'})`,
        size: item.size_gb || 0
      }));
    
    return {
      labels: topProducts.map(product => product.label),
      datasets: [{
        label: 'Log Size (GB)',
        data: topProducts.map(product => product.size),
        backgroundColor: Array(10).fill().map((_, i) => getColor(i, 0.7)),
        borderColor: Array(10).fill().map((_, i) => getColor(i)),
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  }, [clientLogs, dataVersion]);
  
  // Optimized host status data preparation
  const hostStatusData = useMemo(() => {
    if (!hostClients?.hosts?.length) return null;
    
    const counts = hostClients.hosts.reduce((acc, host) => {
      acc[host.status] = (acc[host.status] || 0) + 1;
      return acc;
    }, {});
    
    return {
      labels: ['Active', 'Inactive'],
      datasets: [{
        data: [counts.active || 0, counts.inactive || 0],
        backgroundColor: [
          alpha(theme.palette.success.main, 0.7),
          alpha(theme.palette.error.main, 0.7)
        ],
        borderColor: [
          theme.palette.success.main,
          theme.palette.error.main
        ],
        borderWidth: 1,
      }],
    };
  }, [hostClients, theme.palette.error.main, theme.palette.success.main, dataVersion]);
  
  // Chart options (memoized to prevent re-renders)
  const lineChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          color: theme.palette.text.secondary,
          font: {
            size: 10,
          },
        },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.text.primary, 0.1),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: alpha(theme.palette.text.secondary, 0.1),
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 4,
      },
      line: {
        borderWidth: 2,
      },
    },
    animation: false, // Disable animations for better performance
  }), [theme.palette]);

  const pieChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.text.primary, 0.1),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
      },
    },
    animation: false, // Disable animations for better performance
  }), [theme.palette]);

  const barChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.text.primary, 0.1),
        borderWidth: 1,
        padding: 8,
        boxPadding: 4,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Log Size (GB)',
          color: theme.palette.text.secondary,
          font: {
            size: 12,
          },
        },
        grid: {
          color: alpha(theme.palette.text.secondary, 0.05),
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
        }
      },
      y: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
        }
      }
    },
    animation: false, // Disable animations for better performance
  }), [theme.palette]);
  
  // Helper function to get colors
  function getColor(index, alpha = 1) {
    const colors = [
      `rgba(58, 134, 255, ${alpha})`,   // blue - #3a86ff (primary)
      `rgba(76, 201, 189, ${alpha})`,   // teal - #4cc9bd (secondary)
      `rgba(255, 183, 77, ${alpha})`,   // amber - #ffb74d (tertiary)
      `rgba(255, 153, 102, ${alpha})`,  // soft orange - #ff9966 (quaternary)
      `rgba(76, 175, 80, ${alpha})`,    // green
      `rgba(66, 165, 245, ${alpha})`,   // light blue
      `rgba(126, 87, 194, ${alpha})`,   // purple
      `rgba(240, 98, 146, ${alpha})`,   // pink
      `rgba(103, 58, 183, ${alpha})`,   // deep purple
      `rgba(0, 150, 136, ${alpha})`,    // teal
    ];
    return colors[index % colors.length];
  }

  // Check if we're still loading initial data
  const isLoading = useMemo(() => {
    return (
      !isDataLoaded || 
      loading.summary || 
      loading.hosts || 
      loading.systemMetrics || 
      loading.clientLogs || 
      loading.hostClients || 
      loading.clientDetails
    );
  }, [
    isDataLoaded,
    loading.summary,
    loading.hosts,
    loading.systemMetrics,
    loading.clientLogs,
    loading.hostClients,
    loading.clientDetails
  ]);

  // Calculate active services percentage
  const activeServicesPercentage = useMemo(() => {
    if (!summary || !summary.total_hosts) return 0;
    return Math.round((summary.active_services / summary.total_hosts) * 100);
  }, [summary]);

  // Update where client names are displayed
  const renderClientName = (clientName) => {
    return getFullClientName(clientName);
  };

  // Update chart data preparation
  const prepareChartData = (data) => {
    if (!data) return null;

    const rows = data.filter((item) => !isExcludedInternalClient(item.client_name));

    return {
      labels: rows.map(item => getFullClientName(item.client_name)),
      datasets: [{
        label: "Log Size (GB)",
        data: rows.map(item => item.size_gb),
        backgroundColor: Array(rows.length).fill().map((_, i) => alpha(getColor(i), 0.7)),
        borderColor: Array(rows.length).fill().map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  };

  // Update product chart data
  const prepareProductChartData = (data) => {
    if (!data) return null;

    const rows = data.filter((item) => !isExcludedInternalClient(item.client_name));

    return {
      labels: rows.map(item => `${item.product} (${getFullClientName(item.client_name)})`),
      datasets: [{
        label: "Log Size (GB)",
        data: rows.map(item => item.size_gb),
        backgroundColor: Array(rows.length).fill().map((_, i) => alpha(getColor(i), 0.7)),
        borderColor: Array(rows.length).fill().map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  };

  return (
    <Box>
      {/* Region Header */}
      <Box
        sx={{
          mb: 4,
          borderRadius: '20px',
          padding: { xs: 2, md: 3 },
          backgroundImage: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" mb={0.5}>
            
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {regionId} Region
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="text.secondary">
            Detailed monitoring information for {regionId} region
          </Typography>
        </Box>
        
        <Box display="flex" gap={2} alignItems="center">
        <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToDashboard}
              sx={{
                mr: 2,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 'medium',
                borderColor: alpha(theme.palette.primary.main, 0.3),
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              Dashboard
            </Button>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Host</InputLabel>
            <Select
              value={host}
              label="Host"
              onChange={(e) => setHost(e.target.value)}
              sx={{
                borderRadius: '10px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
              }}
            >
              <MenuItem value="">All Hosts</MenuItem>
              {regionHosts.map((hostname) => (
                <MenuItem key={hostname} value={hostname}>
                  {hostname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
              sx={{
                borderRadius: '10px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
              }}
            >
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="6h">Last 6 Hours</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={handleRefresh}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Remove LoadingOverlay wrapper and display content directly */}
      <Box sx={{ mt: 3 }}>
        {/* Top row */}
        <Grid container spacing={3} mb={4}>
          {/* Hosts Card */}
          <Grid item xs={12} sm={6} lg={3}>
            <ModernMetricCard
              title="Hosts"
              value={summary?.total_hosts || 0}
              subtitle="Total hosts in region"
              icon={ServerIcon}
              color="primary"
            />
          </Grid>
          
          {/* Active Services Card */}
          <Grid item xs={12} sm={6} lg={3}>
            <ModernMetricCard
              title="Services"
              value={summary?.active_services || 0}
              subtitle={`${activeServicesPercentage}% active`}
              icon={CheckCircleIcon}
              trend={activeServicesPercentage > 80 ? "up" : "down"}
              trendValue={`${activeServicesPercentage}%`}
              color="secondary"
            />
          </Grid>
          
          {/* Clients Card */}
          <Grid item xs={12} sm={6} lg={3}>
            <ModernMetricCard
              title="Clients"
              value={summary?.total_clients || 0}
              subtitle="Total clients with logs"
              icon={BusinessIcon}
              color="tertiary"
            />
          </Grid>
          
          {/* Log Size Card */}
          <Grid item xs={12} sm={6} lg={3}>
            <ModernMetricCard
              title="Log Size"
              value={(summary?.total_log_size || 0).toFixed(1)}
              subtitle="GB total"
              icon={MemoryIcon}
              color="quaternary"
            />
          </Grid>
        </Grid>
        
        {/* Charts */}
        <Grid container spacing={3}>
          {/* Host Status Chart */}
          <Grid item xs={12} md={6}>
            <ModernChartCard
              title="Host Status"
              subtitle="Active vs. Inactive hosts"
              chart={hostStatusData ? (
                <Pie
                  data={hostStatusData}
                  options={pieChartOptions}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No host data available</Typography>
                </Box>
              )}
            />
          </Grid>
          
          {/* System Metrics Chart */}
          <Grid item xs={12} md={6}>
            <ModernChartCard
              title="System Metrics"
              subtitle={`CPU & Memory usage (${timeRange})`}
              chart={systemMetricsData ? (
                <Line
                  data={systemMetricsData}
                  options={lineChartOptions}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No metrics data available</Typography>
                </Box>
              )}
              actions={
                <Tooltip title="Change time range">
                  <IconButton
                    size="small"
                    onClick={() => setTimeRange(
                      timeRange === '24h' ? '7d' : 
                      timeRange === '7d' ? '30d' : '24h'
                    )}
                  >
                    <DateRangeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
          </Grid>
          
          {/* Client Log Size Chart */}
          <Grid item xs={12} md={6}>
            <ModernChartCard
              title="Client Log Size"
              subtitle="Top 5 clients by log size"
              chart={clientLogData ? (
                <Bar
                  data={clientLogData}
                  options={barChartOptions}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No client data available</Typography>
                </Box>
              )}
            />
          </Grid>
          
          {/* Product Distribution Chart */}
          <Grid item xs={12} md={6}>
            <ModernChartCard
              title="Product Distribution"
              subtitle="Log size by product"
              chart={productLogData ? (
                <Bar
                  data={productLogData}
                  options={barChartOptions}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No product data available</Typography>
                </Box>
              )}
            />
          </Grid>
        </Grid>
        
        {/* Host List Section */}
        <Box mt={4}>
          <Grid container alignItems="center" spacing={2} mb={2}>
            <Grid item>
              <Typography variant="h6" fontWeight="medium">
                Hosts
              </Typography>
            </Grid>
            <Grid item>
              <Chip
                label={`${summary?.total_hosts || 0} total`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 'medium' }}
              />
            </Grid>
            <Grid item xs />
            <Grid item>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 2,
                }}
                onClick={() => navigate(`/regions/${regionId}/hosts`)}
              >
                View All
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}